import { Theme } from '@material-ui/core';
import {
  Box,
  Button,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';

import { buttonContainer } from '../../../assets/styles/whistleSidebarButton.style';
import arrowUpRightIcon from '../../../assets/svg/arrowUpRightIcon.svg';
import clipboardIcon from '../../../assets/svg/clipboardIcon.svg';
import clipboardIconActive from '../../../assets/svg/clipboardIconActive.svg';
import inboxIcon from '../../../assets/svg/inboxIcon.svg';
import inboxIconActive from '../../../assets/svg/inboxIconActive.svg';
import knowledgeBaseIcon from '../../../assets/svg/knowledgBaseIcon.svg';
import logoutIcon from '../../../assets/svg/logOutIcon.svg';
import meetingIcon from '../../../assets/svg/meetingIconGray.svg';
import meetingIconSelect from '../../../assets/svg/meetingIconGreen.svg';
import menuIcon from '../../../assets/svg/menuIcon.svg';
import menuIconActive from '../../../assets/svg/menuIconActive.svg';
import saveIcon from '../../../assets/svg/saveIcon.svg';
import saveIconActive from '../../../assets/svg/saveIconActive.svg';
import settingsIcon from '../../../assets/svg/settingsIcon.svg';
import settingsIconActive from '../../../assets/svg/settingsIconActive.svg';
import LanguageSelector from '../../common/language/languageSelector';

interface WhistleSidebarProps {
  isOpenMobile: boolean;
}

const WhistleSidebar = ({ isOpenMobile }: WhistleSidebarProps) => {
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const location = useLocation();
  const pathname = location.pathname;

  const MINI_WIDTH = 78;
  const EXPANDED_WIDTH = 240;
  const MINI_BUTTON_WIDTH = 46;
  const EXPANDED_BUTTON_WIDTH = 208;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isExpanded, setExpanded] = useState(false);

  const handleButtonClick = (buttonClicked: string) => {
    navigate('/' + buttonClicked);
  };

  const handleToggleExpand = () => {
    setExpanded((oldValue) => !oldValue);
  };

  const urlIncludes = (specificPath: string) => {
    return pathname.includes(`/${specificPath}`);
  };

  const getInboxIcon = () => {
    return urlIncludes('inbox') ? inboxIconActive : inboxIcon;
  };

  const getNewReportIcon = () => {
    return urlIncludes('new-report') ? clipboardIconActive : clipboardIcon;
  };

  const getDraftIcon = () => {
    return urlIncludes('drafts') ? saveIconActive : saveIcon;
  };

  const getSettingsIcon = () => {
    return urlIncludes('settings') ? settingsIconActive : settingsIcon;
  };

  const getMeetingsIcon = () => {
    return urlIncludes('meeting-list') ? meetingIconSelect : meetingIcon;
  };

  const menuButtonStyles = () => {
    const miniStyles = {
      minWidth: MINI_BUTTON_WIDTH,
      marginBottom: 3,
      justifyContent: 'center',
      height: '46px',
    };
    const expandedStyles = {
      ...buttonContainer,
      minWidth: EXPANDED_BUTTON_WIDTH,
      justifyContent: 'flex-start',
      marginBottom: 3,
    };

    return isExpanded ? expandedStyles : miniStyles;
  };

  const responsiveButtonStyles = {
    width: isMobile || isExpanded ? EXPANDED_BUTTON_WIDTH : MINI_BUTTON_WIDTH,
    minWidth:
      isMobile || isExpanded ? EXPANDED_BUTTON_WIDTH : MINI_BUTTON_WIDTH,
    justifyContent: isMobile || isExpanded ? 'flex-start' : 'center',
  };

  const handleLogout = async () => {
   /**
     * We removed this two calls in favor of
     * using the `/logout` route to handle the logout process easily since
     * it consolidates all the logout logic in one place.
     * also it is accessible wherever the user is in the app. we can easily log them
     * out by just navigating to `/logout` route.
     */ 
    // await authService.logout();
    // logout();
    navigate('/logout');
  };

  return (
    <Box
      flexBasis={{
        xs: 0,
        sm: isExpanded ? EXPANDED_WIDTH : MINI_WIDTH,
      }}
      flexShrink={0}
      display={{
        xs: isOpenMobile ? 'block' : 'none',
        sm: 'block',
      }}
    >
      <Drawer
        variant='permanent'
        open={isExpanded}
        PaperProps={{
          sx: {
            backgroundColor: '#181818',
            padding: 1,
            zIndex: 900,
            paddingTop: '90px',

            width: {
              xs: EXPANDED_WIDTH,
              sm: isExpanded ? EXPANDED_WIDTH : MINI_WIDTH,
            },
            gap: 2,
          },
        }}
      >
        <Box sx={{ flex: 1 }}>
          {/* Menu Button */}
          <Button
            startIcon={isExpanded && <img src={menuIconActive} alt='Menu' />}
            sx={{
              ...menuButtonStyles(),
              display: { xs: 'none', sm: 'flex' },
            }}
            onClick={handleToggleExpand}
          >
            {isExpanded ? (
              <p>
                <FormattedMessage defaultMessage='Menu' />
              </p>
            ) : (
              <img src={menuIcon} alt='Menu' />
            )}
          </Button>

          {/* Inbox Button */}
          <Button
            startIcon={
              (isMobile || isExpanded) && (
                <img src={getInboxIcon()} alt='Inbox' />
              )
            }
            sx={{
              ...buttonContainer,
              ...responsiveButtonStyles,
              backgroundColor: urlIncludes('inbox')
                ? 'rgba(0, 206, 207, 0.2)'
                : '', // Change colors as needed
              '&:hover': {
                backgroundColor: 'rgba(0, 206, 207, 0.2)',
              },
              color: urlIncludes('inbox') ? '#00CECF' : '#C0C0C0',
            }}
            onClick={() => handleButtonClick('inbox')}
          >
            {isMobile || isExpanded ? (
              <p>
                <FormattedMessage defaultMessage='Inbox' />
              </p>
            ) : (
              <img src={getInboxIcon()} alt='Inbox' />
            )}
          </Button>

          {/* Create Report button */}
          <Button
            startIcon={
              (isMobile || isExpanded) && (
                <img src={getNewReportIcon()} alt='Create report' />
              )
            }
            sx={{
              ...buttonContainer,
              ...responsiveButtonStyles,
              backgroundColor: urlIncludes('new-report')
                ? 'rgba(0, 206, 207, 0.2)'
                : '', // Change colors as needed
              '&:hover': {
                backgroundColor: 'rgba(0, 206, 207, 0.2)',
              },
              color: urlIncludes('new-report') ? '#00CECF' : '#C0C0C0',
            }}
            onClick={() => handleButtonClick('new-report')}
          >
            {isMobile || isExpanded ? (
              <p>
                <FormattedMessage defaultMessage='Create Report' />
              </p>
            ) : (
              <img src={getNewReportIcon()} alt='Create report' />
            )}
          </Button>

          {/* Draft Report button */}
          <Button
            startIcon={
              (isMobile || isExpanded) && (
                <img src={getDraftIcon()} alt='Draft report' />
              )
            }
            sx={{
              ...buttonContainer,
              ...responsiveButtonStyles,
              backgroundColor: urlIncludes('drafts')
                ? 'rgba(0, 206, 207, 0.2)'
                : '', // Change colors as needed
              '&:hover': {
                backgroundColor: 'rgba(0, 206, 207, 0.2)',
              },
              color: urlIncludes('drafts') ? '#00CECF' : '#C0C0C0',
            }}
            onClick={() => handleButtonClick('drafts')}
          >
            {isMobile || isExpanded ? (
              <p>
                <FormattedMessage defaultMessage='Draft Report' />
              </p>
            ) : (
              <img src={getDraftIcon()} alt='Draft report' />
            )}
          </Button>

          {/* Meetings button */}
          <Button
            startIcon={
              (isMobile || isExpanded) && (
                <img src={getMeetingsIcon()} alt='Draft report' />
              )
            }
            sx={{
              ...buttonContainer,
              ...responsiveButtonStyles,
              backgroundColor: urlIncludes('meeting-list')
                ? 'rgba(0, 206, 207, 0.2)'
                : '', // Change colors as needed
              '&:hover': {
                backgroundColor: 'rgba(0, 206, 207, 0.2)',
              },
              color: urlIncludes('meeting-list') ? '#00CECF' : '#C0C0C0',
            }} 
            onClick={() => handleButtonClick('meeting-list')}
          >
            {isMobile || isExpanded ? (
            <p>
              <FormattedMessage defaultMessage='Meetings' />
            </p>
            ) : (
            <img src={getMeetingsIcon()} alt='Draft report' />
            )}
          </Button>

          {/* Knowledge base Button */}
          <Button
            startIcon={
              (isMobile || isExpanded) && (
                <img src={knowledgeBaseIcon} alt='Knowledge base' />
              )
            }
            sx={{
              ...buttonContainer,
              ...responsiveButtonStyles,
              backgroundColor: urlIncludes('knowledge-base')
                ? 'rgba(0, 206, 207, 0.2)'
                : '', // Change colors as needed
              '&:hover': {
                backgroundColor: 'rgba(0, 206, 207, 0.2)',
              },
              color: urlIncludes('knowledgebase') ? '#00CECF' : '#C0C0C0',
            }}
            onClick={() => handleButtonClick('knowledge-base')}
          >
            {isMobile || isExpanded ? (
              <Stack direction='row' spacing={0.5}>
                <p>
                  <FormattedMessage defaultMessage='Knowledge base' />
                </p>
                <img src={arrowUpRightIcon} alt='arrowUpRight-icon' />
              </Stack>
            ) : (
              <img src={knowledgeBaseIcon} alt='Knowledge base' />
            )}
          </Button>

          {/* Settings Button */}
          <Button
            startIcon={
              (isMobile || isExpanded) && (
                <img src={getSettingsIcon()} alt='Settings' />
              )
            }
            sx={{
              ...buttonContainer,
              ...responsiveButtonStyles,
              paddingX: '15px',
              backgroundColor: urlIncludes('settings')
                ? 'rgba(0, 206, 207, 0.2)'
                : '', // Change colors as needed
              '&:hover': {
                backgroundColor: 'rgba(0, 206, 207, 0.2)',
              },
              color: urlIncludes('/settings') ? '#00CECF' : '#C0C0C0',
            }}
            onClick={() => handleButtonClick('settings')}
          >
            {isMobile || isExpanded ? (
              <p>
                <FormattedMessage defaultMessage='Settings' />
              </p>
            ) : (
              <img src={getSettingsIcon()} alt='Settings' />
            )}
          </Button>
        </Box>

        {isExpanded || isOpenMobile ? (
          <>
            <Stack sx={{ marginBottom: '28px', alignItems: 'center' }}>
              <LanguageSelector variant='light' />
              <hr
                style={{
                  border: 'none',
                  backgroundColor: 'rgba(88, 88, 88, 1)',
                  height: '1px',
                  width: '208px',
                  marginBottom: '12px',
                  marginTop: '12px',
                }}
              />
              <Button
                onClick={handleLogout}
                startIcon={
                  <img
                    src={logoutIcon}
                    alt='logout-icon'
                    style={{ height: '22px', marginLeft: '12px' }}
                  />
                }
                sx={{ width: '100%', display: 'flex', justifyContent: 'left' }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Schibsted Grotesk',
                    fontWeight: 500,
                    fontSize: '16px',
                    textTransform: 'none',
                    color: 'rgba(192, 192, 192, 1)',
                    ml: '16px',
                  }}
                >
                  <FormattedMessage defaultMessage='Logout' />
                </Typography>
              </Button>
            </Stack>
          </>
        ) : (
          ''
        )}
      </Drawer>
    </Box>
  );
};

export default WhistleSidebar;
